import {
  type AuthenticationClient,
  type DirectusUser,
  type RestClient,
  type DirectusClient as _DirectusClient,
  authentication,
  createDirectus,
  createItem,
  customEndpoint,
  deleteFile,
  deleteItem,
  isDirectusError,
  passwordRequest,
  passwordReset,
  readFile,
  readFiles,
  readItem,
  readItems,
  readMe,
  readSingleton,
  rest,
  updateFile,
  updateItem,
  updateMe,
  uploadFiles,
} from '@directus/sdk';
import type { TemplateDTO } from '@funnellab/shared/src/dtos/creativeBuilder/TemplateDTO';
import {
  type ChatbotAnswerResponseDTO,
  type ChatbotConfigurationDTO,
  type ChatbotConfigurationDTOForUpdate,
} from '@funnellab/shared/src/dtos/chatbot/ChatbotConfigurationDTO.ts';
import { type ChatbotApplicantDTO } from '@funnellab/shared/src/dtos/chatbot/ChatbotApplicantDTO.ts';

import { Environment, isTest } from '../config/environment.ts';
import {
  type JobDetailsDTO,
  type JobForWizard,
  type JobWizardDTO,
  detailRelations,
  wizardRelations,
} from '@src/api/queries/job.ts';
import {
  type Language,
  type OrganizationRole,
} from '@funnellab/shared/src/types/directus-enums.ts';
import { NotFoundError } from '@src/api/api.errors.ts';
import {
  type OrganizationDto,
  organizationQuery,
} from '@src/api/queries/organization.ts';
import { Paths } from '@src/config/paths.ts';
import {
  addQueryParams,
  getArrayParams,
  getQueryParamsFromArray,
} from './utils/queryParamsFromArray.ts';
import { castNullToUndefined } from '@utils/castNullToUndefined.ts';
import { directusUsersQuery } from '@src/api/queries/directusUsers.ts';
import { employerBrandQuery } from '@src/api/queries/employerBrand.ts';
import { generateId } from '@funnellab/shared/src/utils/id.ts';
import { secureFetch } from './secureFetch.ts';
import { simpleEntityQueryWithRelevanceAndLanguage } from '@src/api/queries/simpleEntity.ts';
import type {
  ApplicantDTO,
  ApplicantDTOForCreation,
  ApplicantDTOForUpdate,
  ApplicantLabelDTO,
  ApplicantLabelDTOForCreation,
  ApplicantLabelDTOForUpdate,
} from '@funnellab/shared/src/dtos/applications/ApplicantDTO.ts';
import type { ApplicationPhaseDTO } from '@funnellab/shared/src/dtos/applications/ApplicationPhaseDTO';
import type {
  AutoCompleteCollection,
  AutoCompleteResult,
} from '@funnellab/shared/src/types/autocomplete';
import type {
  AutocompleteResult,
  Coordinates,
  PlaceData,
} from '@funnellab/shared/src/types/location.ts';
import type {
  BenefitDto,
  CountryDTO,
  EmployerBrandDto,
  PaginationData,
} from '@src/api/api.types.ts';
import type {
  CustomDirectusTypes,
  DirectusFiles,
  EmployerBrand,
  Organization,
} from '@funnellab/shared/src/types/directus.ts';
import type {
  JobDTO,
  JobDTOForUpdate,
  JobDTORelationsResolved,
} from '@funnellab/shared/src/dtos/JobDTO.ts';
import type { JobImportDTO } from '@funnellab/shared/src/dtos/JobImportDTO';
import type { PaymentSessionDTO } from '@funnellab/shared/src/dtos/PaymentSessionDTO';
import type { RecruiterDTO } from '@funnellab/shared/src/types/recruiters.ts';
import type { SuggestionDTO } from '@funnellab/shared/src/dtos/SuggestionDTO';
import { DirectusErrorCode } from '@funnellab/shared/src/types/errorCodes.ts';
import type { RegistrationDataDTO } from '@funnellab/shared/src/dtos/auth/RegistrationDataDTO.ts';

type DirectusClientWithExtensions<T extends object> = _DirectusClient<T> &
  RestClient<T> &
  AuthenticationClient<T>;

export class DirectusClient {
  readonly _client: DirectusClientWithExtensions<CustomDirectusTypes>;

  constructor(
    providedDirectusClient?: DirectusClientWithExtensions<CustomDirectusTypes>,
  ) {
    if (providedDirectusClient) {
      this._client = providedDirectusClient;
    } else {
      if (!Environment.DIRECTUS_API_URL) {
        throw new Error('Missing environment variable DIRECTUS_API_URL');
      }
      this._client = createDirectus<CustomDirectusTypes>(
        Environment.DIRECTUS_API_URL,
        { globals: { fetch: secureFetch } },
      )
        .with(rest({ credentials: 'include' }))
        .with(
          authentication('session', {
            credentials: 'include',
            autoRefresh: true,
          }),
        );
    }
  }

  /* Authentication */
  async login(email: string, password: string) {
    return this._client.login({ email, password });
  }

  logout() {
    return this._client.logout();
  }

  async isLoggedIn() {
    try {
      const user = await this.getCurrentUser();
      return !!user;
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'status' in e && e?.status === 401) {
        return false;
      }
      if (
        isDirectusError(e) &&
        e.errors.some(
          (error) =>
            error.extensions.code ===
              (DirectusErrorCode.INVALID_CREDENTIALS as string) ||
            error.extensions.code ===
              (DirectusErrorCode.TOKEN_EXPIRED as string),
        )
      ) {
        return false;
      }
      throw e;
    }
  }

  async refresh() {
    return this._client.refresh();
  }

  async deleteCurrentUser(id: string) {
    return this._client.request(
      customEndpoint({
        path: 'user/delete',
        method: 'DELETE',
        body: JSON.stringify({ userId: id }),
      }),
    );
  }

  async registerUser(
    data: RegistrationDataDTO,
  ): Promise<{ organizationId: string | null }> {
    return this._client.request(
      customEndpoint<{ organizationId: string | null }>({
        path: 'registration',
        method: 'POST',
        body: JSON.stringify(data),
      }),
    );
  }

  async registerUserVerify(token: string) {
    await this._client.request(
      customEndpoint({
        path: 'registration/verify',
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    );
  }

  async resendVerification(redirectUrl: string | undefined) {
    return this._client.request(
      customEndpoint<void>({
        path: 'registration/resendVerification',
        method: 'POST',
        body: JSON.stringify({
          redirectUrl,
        }),
      }),
    );
  }

  getCurrentUser() {
    return this._client.request(readMe(directusUsersQuery));
  }

  async hasActiveJobs(userId: string) {
    const { hasActiveJobs } = await this._client.request(
      customEndpoint<{ hasActiveJobs: boolean }>({
        path: 'user/hasActiveJobs',
        method: 'GET',
        params: { userId },
      }),
    );
    return hasActiveJobs;
  }

  async autoComplete(
    collection: AutoCompleteCollection,
    query: string,
    language: Language,
    additionalFields?: string[],
  ) {
    return this._client.request(
      customEndpoint<AutoCompleteResult[]>({
        path: getQueryParamsFromArray(
          '/autocomplete',
          'additionalFields',
          additionalFields,
        ),
        method: 'GET',
        params: { collection, query, language },
      }),
    );
  }

  async isContactInJob(userId: string) {
    const { isContactInJob } = await this._client.request(
      customEndpoint<{ isContactInJob: boolean }>({
        path: 'user/isContactInJob',
        method: 'GET',
        params: { userId },
      }),
    );
    return isContactInJob;
  }

  async updateCurrentUser(user: Partial<DirectusUser<CustomDirectusTypes>>) {
    return this._client.request(updateMe(user, directusUsersQuery));
  }

  async requestPasswordReset(email: string, redirectUrl?: string) {
    const url = new URL(`${Environment.APP_URL}${Paths.RESET}`);
    if (redirectUrl) {
      url.searchParams.append('redirectUrl', redirectUrl);
    }
    return this._client.request(passwordRequest(email, url.toString()));
  }

  async resetPassword(token: string, password: string) {
    return this._client.request(passwordReset(token, password));
  }

  /* File Handling */
  async getFile(id?: string) {
    const item = id ? await this._client.request(readFile(id)) : null;
    if (!item) {
      throw new NotFoundError();
    }
    return item as DirectusFiles;
  }

  async uploadFile(file: File, folder?: string): Promise<DirectusFiles> {
    const id = generateId();
    const formData = new FormData();

    formData.append('id', id);
    if (folder) {
      formData.append('folder', folder);
    }
    formData.append('file', file, file.name);
    return (await this._client.request(
      uploadFiles(formData),
    )) as unknown as Promise<DirectusFiles>;
  }

  async deleteFile(id: string) {
    return this._client.request(deleteFile(id));
  }

  getFileDownloadUrl(id: string) {
    return `${this._client.url}assets/${id}`;
  }

  listFilesInFolder(
    folderId: string,
    queryData?: PaginationData,
  ): Promise<DirectusFiles[]> {
    const data = {
      filter: {
        folder: { _eq: folderId },
        title: queryData?.query ? { _icontains: queryData?.query } : undefined,
      },
      limit: queryData?.perPage ?? 1000,
      page: (queryData?.page ?? 0) + 1, // Directus starts counting at 1...
    };
    return this._client.request(readFiles(data)) as Promise<DirectusFiles[]>;
  }

  moveFileToFolder(fileId: string, folderId: string): Promise<DirectusFiles> {
    return this._client.request(
      updateFile(fileId, { folder: folderId }),
    ) as Promise<DirectusFiles>;
  }

  /* Recruiter */
  async getRecruitersByOrganization(organizationId?: string) {
    return this._client.request(
      customEndpoint<RecruiterDTO[]>({
        path: 'recruiter',
        method: 'GET',
        params: { organizationId: organizationId },
      }),
    );
  }

  async acceptOrganizationInvite(
    organization: string,
    token: string,
  ): Promise<void> {
    return this._client.request(
      customEndpoint<void>({
        path: 'invite/organization/accept',
        method: 'POST',
        body: JSON.stringify({ organization, token }),
      }),
    );
  }

  /* Country */
  async getCountries(): Promise<CountryDTO[]> {
    return this._client.request<CountryDTO[]>(
      readItems('country', {
        sort: 'label',
        fields: ['id', 'label', 'subdivision_code', 'country_code'],
      }),
    );
  }

  async getCountryById(countryId: string): Promise<CountryDTO> {
    return this._client.request<CountryDTO>(
      readItem('country', countryId, {
        fields: ['id', 'label', 'subdivision_code', 'country_code'],
      }),
    );
  }

  async getCountriesByCountryCode(countryCode: string): Promise<CountryDTO[]> {
    return this._client.request<CountryDTO[]>(
      readItems('country', {
        filter: { country_code: { _eq: countryCode } },
        fields: ['id', 'label', 'subdivision_code', 'country_code'],
      }),
    );
  }

  async getCountriesBySubdivisionCode(
    subdivisionCode: string,
  ): Promise<CountryDTO[]> {
    return this._client.request<CountryDTO[]>(
      readItems('country', {
        filter: { subdivision_code: { _eq: subdivisionCode } },
        fields: ['id', 'label', 'subdivision_code', 'country_code'],
      }),
    );
  }

  /* Organization */
  async getAllOrganisations() {
    return this._client.request(readItems('organization', organizationQuery));
  }

  async getOrganizationById(organizationId: string) {
    return this._client.request(
      readItem('organization', organizationId, organizationQuery),
    );
  }

  createOrganization(
    organization: Partial<Organization>,
  ): Promise<OrganizationDto> {
    return this._client.request<OrganizationDto>(
      createItem('organization', organization, organizationQuery),
    );
  }

  async updateOrganization(
    organizationId: string,
    organization: Partial<Organization>,
  ) {
    return this._client.request(
      updateItem(
        'organization',
        organizationId,
        organization,
        organizationQuery,
      ),
    );
  }

  async deleteOrganization(organizationId: string) {
    return this._client.request(
      customEndpoint({
        path: `/organizations/${organizationId}`,
        method: 'DELETE',
      }),
    );
  }

  /* Employer Brand */
  async getAllEmployerBrands() {
    return this._client.request(readItems('employerBrand', employerBrandQuery));
  }

  async getPossibleEmployerBrands(orgId: string) {
    return this._client.request(
      customEndpoint<Pick<EmployerBrandDto, 'id' | 'name'>[]>({
        path: addQueryParams('employerBrand', { organizationId: orgId }),
        method: 'GET',
      }),
    );
  }

  async createEmployerBrand(employerBrand: Partial<EmployerBrand>) {
    return this._client.request(
      createItem('employerBrand', employerBrand, employerBrandQuery),
    );
  }

  async getEmployerBrand(empBrandId: string) {
    return this._client.request(
      readItem('employerBrand', empBrandId, employerBrandQuery),
    );
  }

  async updateEmployerBrand(
    employerBrandId: string,
    employerBrandData: Partial<EmployerBrandDto>,
  ) {
    return this._client.request(
      updateItem(
        'employerBrand',
        employerBrandId,
        employerBrandData,
        employerBrandQuery,
      ),
    );
  }

  async createEmployerBrandBenefit(
    employerBrand_id: string,
    benefits_id: string,
  ) {
    return this._client.request(
      createItem('employerBrand_benefits', {
        employerBrand_id,
        benefits_id,
      }),
    );
  }

  async removeEmployerBrandBenefit(id: number) {
    return this._client.request(deleteItem('employerBrand_benefits', id));
  }

  addRecruiter(organizationId: string, contactEmail: string) {
    return this._client.request(
      customEndpoint<RecruiterDTO>({
        path: 'recruiter/add',
        method: 'POST',
        body: JSON.stringify({ organizationId, contactEmail }),
      }),
    );
  }

  removeRecruiter(organizationId: string, recruiterId: string) {
    return this._client.request<void>(
      customEndpoint({
        path: 'recruiter/remove',
        method: 'POST',
        body: JSON.stringify({ organizationId, recruiterId }),
      }),
    );
  }

  removeInvitedRecruiter(organizationId: string, recruiterId: string) {
    return this._client.request<void>(
      customEndpoint({
        path: `organizations/${organizationId}/recruiter/invites/${recruiterId}`,
        method: 'DELETE',
      }),
    );
  }

  resendInvite(organizationId: string, recruiterId: string) {
    return this._client.request<void>(
      customEndpoint({
        path: `organizations/${organizationId}/recruiter/invites/${recruiterId}/resend`,
        method: 'POST',
      }),
    );
  }

  updateRecruiterRole(
    organizationId: string,
    recruiterId: string,
    role: `${OrganizationRole}`,
  ) {
    return this._client.request(
      customEndpoint<void>({
        path: 'recruiter/updateRole',
        method: 'POST',
        body: JSON.stringify({ organizationId, recruiterId, role }),
      }),
    );
  }

  createBenefit(benefit: Partial<BenefitDto>) {
    return this._client.request(
      createItem(
        'benefits',
        benefit,
        simpleEntityQueryWithRelevanceAndLanguage,
      ),
    );
  }

  getJobForWizzard(id: string) {
    return this._client.request(
      customEndpoint<JobWizardDTO>({
        path: getQueryParamsFromArray(
          `jobs/${id}`,
          'relations',
          wizardRelations,
        ),
        method: 'GET',
      }),
    );
  }

  getJobForDetailView(id: string) {
    return this._client.request(
      customEndpoint<JobDetailsDTO>({
        path: getQueryParamsFromArray(
          `jobs/${id}`,
          'relations',
          detailRelations,
        ),
        method: 'GET',
      }),
    );
  }

  getJob(id: string, relations?: string[]) {
    return this._client.request<JobDTO>(
      customEndpoint({
        path: getQueryParamsFromArray(`jobs/${id}`, 'relations', relations),
        method: 'GET',
      }),
    );
  }

  async createJob(
    job: Partial<
      JobDTO &
        Pick<
          JobDTORelationsResolved,
          'organization' | 'employerBrand' | 'owner'
        >
    >,
  ) {
    const apiSafeJob = castNullToUndefined(job);
    const newJob = await this._client.request(
      createItem('job', {
        ...apiSafeJob,
        employerBrand: job.employerBrand?.id,
        organization: job.organization?.id,
        owner: job.owner?.id,
      }),
    );
    return this.getJobForWizzard(newJob.id);
  }

  deleteJob(id: string) {
    return this._client.request(deleteItem('job', id));
  }

  copyJob(jobId: string) {
    return this._client.request(
      customEndpoint<JobDTO>({
        path: `jobs/${jobId}`,
        method: 'POST',
      }),
    );
  }

  async updateJobForWizard(jobId: string, job: JobDTOForUpdate) {
    return this._client.request(
      customEndpoint<JobWizardDTO>({
        path: getQueryParamsFromArray(
          `jobs/${jobId}`,
          'relations',
          wizardRelations,
        ),
        method: 'PATCH',
        body: JSON.stringify(job),
      }),
    );
  }

  async updateJob(jobId: string, job: JobDTOForUpdate, relations?: string[]) {
    return this._client.request(
      customEndpoint<JobDTO>({
        path: getQueryParamsFromArray(`jobs/${jobId}`, 'relations', relations),
        method: 'PATCH',
        body: JSON.stringify(job),
      }),
    );
  }

  updateJobForDetailsView(jobId: string, job: JobDTOForUpdate) {
    return this._client.request(
      customEndpoint<JobDetailsDTO>({
        path: getQueryParamsFromArray(
          `jobs/${jobId}`,
          'relations',
          detailRelations,
        ),
        method: 'PATCH',
        body: JSON.stringify(job),
      }),
    );
  }

  getLocationAutocompletion(q: string, language: string) {
    return this._client.request(
      customEndpoint<AutocompleteResult[]>({
        path: 'location/autocomplete',
        method: 'GET',
        params: { q, language },
      }),
    );
  }

  getLocationDetails(placeId: string, language: string) {
    return this._client.request(
      customEndpoint<PlaceData>({
        path: 'location/details',
        method: 'GET',
        params: { placeId, language },
      }),
    );
  }

  getLocationDetailsBatch(placeIds: string[], language: string) {
    return this._client.request(
      customEndpoint<Record<string, PlaceData | undefined>>({
        path:
          getQueryParamsFromArray(
            'location/detailsBatch',
            'placeIds',
            placeIds,
          ) + `&language=${language}`,
        method: 'GET',
      }),
    );
  }

  getCoordinates(placeId: string) {
    return this._client.request(
      customEndpoint<Coordinates>({
        path: 'location/coordinates',
        method: 'GET',
        params: { placeId },
      }),
    );
  }

  getCoordinatesBatch(placeIds: string[]) {
    return this._client.request(
      customEndpoint<
        Record<
          string,
          {
            lat: number;
            lng: number;
          } | null
        >
      >({
        path: getQueryParamsFromArray(
          'location/coordinatesBatch',
          'placeIds',
          placeIds,
        ),
        method: 'GET',
      }),
    );
  }

  getSuggestionByJobTitle(title: string) {
    return this._client.request(
      customEndpoint<SuggestionDTO>({
        path: 'suggestions?jobTitle=' + encodeURIComponent(title),
        method: 'GET',
      }),
    );
  }

  async createCIDocument(
    employerBrand_id: string,
    directus_files_id: string,
  ): Promise<EmployerBrandDto> {
    return (
      await this._client.request(
        createItem(
          'employerBrand_files',
          {
            directus_files_id,
            employerBrand_id,
          },
          { fields: [{ employerBrand_id: employerBrandQuery.fields }] },
        ),
      )
    ).employerBrand_id!;
  }

  async removeCIDocument(id: number): Promise<void> {
    return await this._client.request(deleteItem('employerBrand_files', id));
  }

  getSettings() {
    return this._client.request(readSingleton('settings'));
  }

  createJobImport(data: {
    title: string;
    text: string;
    organization?: string;
  }) {
    return this._client.request<JobImportDTO>(
      customEndpoint({
        path: 'jobs/import',
        method: 'POST',
        body: JSON.stringify(data),
      }),
    );
  }

  getJobImport(id: number) {
    return this._client.request<JobImportDTO>(
      customEndpoint({
        path: `jobs/import/${id}`,
        method: 'GET',
      }),
    );
  }

  abortJobImport(id: number) {
    return this._client.request<JobImportDTO>(
      customEndpoint({
        path: `jobs/import/${id}/abort`,
        method: 'POST',
      }),
    );
  }

  startPaymentSession(jobId: string) {
    return this._client.request(
      customEndpoint<PaymentSessionDTO>({
        path: 'payments/startSession',
        method: 'POST',
        body: JSON.stringify({ job: jobId }),
      }),
    );
  }

  getApplicationPhases(jobId: string) {
    return this._client.request<ApplicationPhaseDTO[]>(
      customEndpoint({
        path: `jobs/${jobId}/applications/phases`,
        method: 'GET',
      }),
    );
  }

  updateApplicationPhase(
    jobId: string,
    phaseId: string,
    data: Partial<Pick<ApplicationPhaseDTO, 'label' | 'color'>>,
  ) {
    return this._client.request<ApplicationPhaseDTO>(
      customEndpoint({
        path: `jobs/${jobId}/applications/phases/${phaseId}`,
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    );
  }

  deleteApplicationPhase(jobId: string, phaseId: string) {
    return this._client.request<void>(
      customEndpoint({
        path: `jobs/${jobId}/applications/phases/${phaseId}`,
        method: 'DELETE',
      }),
    );
  }

  updateApplicationPhases(
    jobId: string,
    phases: Partial<ApplicationPhaseDTO>[],
  ) {
    return this._client.request<ApplicationPhaseDTO[]>(
      customEndpoint({
        path: `jobs/${jobId}/applications/phases`,
        method: 'PATCH',
        body: JSON.stringify(phases),
      }),
    );
  }

  getApplicationLabels(jobId: string) {
    return this._client.request<ApplicantLabelDTO[]>(
      customEndpoint({
        path: `jobs/${jobId}/applications/labels`,
        method: 'GET',
      }),
    );
  }

  createApplicationLabel(jobId: string, data: ApplicantLabelDTOForCreation) {
    return this._client.request<ApplicantLabelDTO>(
      customEndpoint({
        path: `jobs/${jobId}/applications/labels`,
        method: 'POST',
        body: JSON.stringify(data),
      }),
    );
  }

  updateApplicationLabel(
    jobId: string,
    labelId: string,
    data: ApplicantLabelDTOForUpdate,
  ) {
    return this._client.request<ApplicantLabelDTO>(
      customEndpoint({
        path: `jobs/${jobId}/applications/labels/${labelId}`,
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    );
  }

  deleteApplicationLabel(jobId: string, labelId: string) {
    return this._client.request<void>(
      customEndpoint({
        path: `jobs/${jobId}/applications/labels/${labelId}`,
        method: 'DELETE',
      }),
    );
  }

  getApplicants(jobId: string, relations: string[] = [], withRejected = false) {
    return this._client.request<ApplicantDTO[]>(
      customEndpoint({
        path: getQueryParamsFromArray(
          `jobs/${jobId}/applications/applicants`,
          'relations',
          relations,
        ),
        method: 'GET',
        params: { withRejected },
      }),
    );
  }

  getApplicantById(
    jobId: string,
    applicantId: string,
    relations: string[] = [],
  ) {
    return this._client.request<ApplicantDTO>(
      customEndpoint({
        path: getQueryParamsFromArray(
          `jobs/${jobId}/applications/applicants/${applicantId}`,
          'relations',
          relations,
        ),
        method: 'GET',
      }),
    );
  }

  updateApplicant(
    jobId: string,
    applicantId: string,
    data: Partial<ApplicantDTOForUpdate>,
    relations: string[] = [],
  ) {
    return this._client.request<ApplicantDTO>(
      customEndpoint({
        path: getQueryParamsFromArray(
          `jobs/${jobId}/applications/applicants/${applicantId}`,
          'relations',
          relations,
        ),
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    );
  }

  createApplicant(
    jobId: string,
    data: Partial<ApplicantDTOForCreation>,
    relations: string[] = [],
  ) {
    return this._client.request<ApplicantDTO>(
      customEndpoint({
        path: getQueryParamsFromArray(
          `jobs/${jobId}/applications/applicants`,
          'relations',
          relations,
        ),
        method: 'POST',
        body: JSON.stringify(data),
      }),
    );
  }

  getChatbotConfigurationForJob(
    jobId: string,
    relations: string[] = [],
    draft: boolean,
  ) {
    return this._client.request<ChatbotConfigurationDTO>(
      customEndpoint({
        path: getQueryParamsFromArray(
          `jobs/${jobId}/chatbot/configuration`,
          'relations',
          relations,
        ),
        method: 'GET',
        params: { draft },
      }),
    );
  }

  updateChatbotConfigurationForJob(
    jobId: string,
    configuration: ChatbotConfigurationDTOForUpdate,
    relations: string[] = [],
  ) {
    return this._client.request<ChatbotConfigurationDTO>(
      customEndpoint({
        path: getQueryParamsFromArray(
          `jobs/${jobId}/chatbot/configuration`,
          'relations',
          relations,
        ),
        method: 'PATCH',
        body: JSON.stringify(configuration),
      }),
    );
  }

  publishChatbotConfigurationForJob(jobId: string, relations: string[] = []) {
    return this._client.request<ChatbotConfigurationDTO>(
      customEndpoint({
        path: getQueryParamsFromArray(
          `jobs/${jobId}/chatbot/configuration/publish`,
          'relations',
          relations,
        ),
        method: 'PATCH',
      }),
    );
  }

  answerChatbot(jobId: string, chatbotApplicant: ChatbotApplicantDTO) {
    return this._client.request<ChatbotAnswerResponseDTO>(
      customEndpoint({
        path: `jobs/${jobId}/chatbot/answer`,
        method: 'POST',
        body: JSON.stringify(chatbotApplicant),
      }),
    );
  }

  getHook(
    jobId: string,
    currentJobState: Pick<
      JobForWizard,
      'targetCareerJumpers' | 'addressing'
    > & {
      targetJobTitle?: string[];
      targetExperience?: string[];
      targetEducation?: string[];
      targetSkill?: string[];
      locations?: string[];
    },
  ) {
    const { targetCareerJumpers, addressing, ...arrayParameters } =
      currentJobState;
    return this._client.request<{ hook: string }>(
      customEndpoint({
        path: `jobs/${jobId}/hook?${getArrayParams(arrayParameters)}`,
        method: 'GET',
        params: {
          targetCareerJumpers,
          addressing,
        },
      }),
    );
  }

  // #region Creative Templates
  getAllCreativeTemplates(relations?: string[]): Promise<TemplateDTO[]> {
    return this._client.request(
      customEndpoint({
        path: getQueryParamsFromArray(
          `/creativeBuilder/templates`,
          'relations',
          relations,
        ),
        method: 'GET',
      }),
    );
  }
  //#endregion
}

export const directusClient = isTest
  ? new DirectusClient(
      createDirectus<CustomDirectusTypes>(Environment.DIRECTUS_API_URL)
        .with(rest())
        .with(authentication('cookie')),
    )
  : new DirectusClient();
